import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

/** Shared member/seller climate controls; irrigation and nutrient logic untouched. */
export default function ClimateAutomationPanel({ siteId, basePath, showAddButton = true }) {
  const [items,setItems]=useState([]);
  const [sensors,setSensors]=useState([]);
  const [relays,setRelays]=useState([]);
  const [editing,setEditing]=useState(null);
  const [busy,setBusy]=useState(false);
  const [busyRowIds,setBusyRowIds]=useState([]);
  const [busyControlIds,setBusyControlIds]=useState([]);
  const [busyRelayIds,setBusyRelayIds]=useState([]);
  const [dialogOpen,setDialogOpen]=useState(false);
  const [collapsedIds,setCollapsedIds]=useState(null);
  const [error,setError]=useState('');
  const [form,setForm]=useState({kind:'temperature',name:'자동온도',sensor_device_id:'',sensor_entity_id:'',relay_device_id:'',relay_entity_id:'',setpoint:25,hysteresis:1,direction:'raise',update_period:5});
  const http=axios;
  const load=useCallback(async()=>{
    if(!siteId)return;
    try {
      const [a,b]=await Promise.all([
        http.get(`${basePath}/climate-automations`,{params:{site_id:siteId},withCredentials:true}),
        http.get(`${basePath}/climate-entities`,{params:{site_id:siteId},withCredentials:true})
      ]);
      // API 재조회 시 MQTT WebSocket이 수신한 실시간 센서/릴레이 값을 보존한다.
      // 기존 자동화의 바인딩이 바뀌었으면 이전 값을 재사용하지 않는다.
      setItems(previous => [...(a.data.automations||[])].sort((left, right) => {
        const rank = kind => kind === 'temperature' ? 0 : kind === 'humidity' ? 1 : 2;
        return rank(left.kind) - rank(right.kind) || Number(left.id) - Number(right.id);
      }).map(row => {
        const prior=previous.find(item => String(item.id)===String(row.id));
        if(!prior)return row;
        const sameSensor=String(prior.sensor_device_id)===String(row.sensor_device_id) && String(prior.sensor_entity_id)===String(row.sensor_entity_id);
        const sameRelay=String(prior.relay_device_id)===String(row.relay_device_id) && String(prior.relay_entity_id)===String(row.relay_entity_id);
        return {
          ...row,
          ...(sameSensor && prior.sensor_value !== null && prior.sensor_value !== undefined ? {sensor_value:prior.sensor_value} : {}),
          ...(sameRelay && ['on','off'].includes(prior.relay_state) ? {relay_state:prior.relay_state} : {}),
        };
      }));
      setSensors(b.data.sensors||[]);setRelays(b.data.relays||[]);
    }catch(e){setError(e.response?.data?.error||e.message);}
  },[siteId,basePath]);
  useEffect(()=>{load();},[load]);
  // 자동관수와 동일한 MQTT WebSocket 상태 수신. 자동화 엔진은 Mycodo Bang-Bang 그대로 사용.
  const bindingsKey=items.map(r=>`${r.id}:${r.sensor_device_id}:${r.sensor_entity_id}:${r.relay_device_id}:${r.relay_entity_id}`).join('|');
  useEffect(()=>{
    if(!siteId || !bindingsKey)return undefined;
    let stopped=false;
    const sockets=[];
    const retryTimers=[];
    const normalize=value=>String(value??'').trim().toLowerCase();
    const match=(entities,id,name,domain)=>(entities||[]).find(e=>{
      if(normalize(e.domain)!==domain && !normalize(e.entity_id).startsWith(`${domain}.`))return false;
      const target=normalize(id);
      if(target && [e.entity_id,e.object_id,e.unique_id].some(x=>normalize(x)===target))return true;
      return Boolean(name && normalize(e.name||e.friendly_name)===normalize(name));
    });
    const ids=[...new Set(items.flatMap(r=>[Number(r.sensor_device_id),Number(r.relay_device_id)]).filter(id=>Number.isInteger(id)&&id>0))];
    const receive=payload=>{
      if(stopped || payload?.type!=='state' || Number(payload.site_id)!==Number(siteId))return;
      const deviceId=Number(payload.device_id);
      const entities=Array.isArray(payload.entities)?payload.entities:[];
      setItems(previous=>previous.map(row=>{
        let next=row;
        if(Number(row.sensor_device_id)===deviceId){
          const sensor=match(entities,row.sensor_entity_id,row.sensor_entity_name,'sensor');
          if(sensor && sensor.value!==null && sensor.value!==undefined && sensor.value!=='' && Number.isFinite(Number(sensor.value))){
            next={...next,sensor_value:Number(sensor.value)};
          }
        }
        if(Number(row.relay_device_id)===deviceId){
          const exactRelay=match(entities,row.relay_entity_id,row.relay_entity_name,'switch');
          // A custom display label may differ from the MQTT discovery name.
          // Only use a device-scoped fallback when exactly one switch exists.
          const deviceSwitches=entities.filter(e=>normalize(e.domain)==='switch' || normalize(e.entity_id).startsWith('switch.'));
          const relay=exactRelay || (deviceSwitches.length===1 ? deviceSwitches[0] : null);
          if(relay){
            const value=normalize(relay.value);
            if(['on','true','1'].includes(value))next={...next,relay_state:'on'};
            if(['off','false','0'].includes(value))next={...next,relay_state:'off'};
          }
        }
        return next;
      }));
    };
    ids.forEach(deviceId=>{
      const connect=()=>{
        if(stopped)return;
        const scheme=window.location.protocol==='https:'?'wss':'ws';
        const socket=new WebSocket(`${scheme}://${window.location.host}/api/seller/smartfarm/esp32/ws?site_id=${encodeURIComponent(siteId)}&device_id=${encodeURIComponent(deviceId)}`);
        sockets.push(socket);
        socket.onmessage=event=>{try{receive(JSON.parse(event.data||'{}'));}catch(e){console.warn('Climate MQTT message:',e);}};
        socket.onclose=()=>{if(!stopped)retryTimers.push(window.setTimeout(connect,2000));};
      };
      connect();
    });
    return ()=>{stopped=true;retryTimers.forEach(id=>window.clearTimeout(id));sockets.forEach(socket=>{socket.onclose=null;socket.close();});};
  },[siteId,basePath,bindingsKey]);
  const change=(key,value)=>setForm(f=>({...f,[key]:value}));
  const create=async()=>{
    setBusy(true);setError('');
    try{
      const payload={...form,site_id:Number(siteId)};
      if(editing){await http.put(`${basePath}/climate-automations/${editing}`,payload,{withCredentials:true});}
      else await http.post(`${basePath}/climate-automations`,payload,{withCredentials:true});
      setEditing(null);setDialogOpen(false);await load();
    }catch(e){setError(e.response?.data?.error||e.message);}finally{setBusy(false);}
  };
  const act=async(row,mode)=>{
    if(busyRowIds.includes(row.id))return;
    if(mode==='delete' && !window.confirm(`${row.name} 자동화를 삭제할까요?`))return;
    setBusyRowIds(ids=>[...ids,row.id]);setBusyControlIds(ids=>[...ids,row.id]);setError('');
    try{
      if(mode==='delete'){
        await http.delete(`${basePath}/climate-automations/${row.id}`,{withCredentials:true});
      }else await http.post(`${basePath}/climate-automations/${row.id}/control`,{activate:!row.active},{withCredentials:true});
      await load();
    }catch(e){setError(e.response?.data?.error||e.message);}finally{setBusyRowIds(ids=>ids.filter(id=>id!==row.id));setBusyControlIds(ids=>ids.filter(id=>id!==row.id));}
  };
  // Bang-Bang이 중지된 경우에만 해당 자동화에 바인딩된 Mycodo Output 제어.
  const toggleRelay=async row=>{
    if(busy || busyRowIds.includes(row.id) || row.active)return;
    const nextState=row.relay_state==='on'?'off':'on';
    setBusyRowIds(ids=>[...ids,row.id]);setBusyRelayIds(ids=>[...ids,row.id]);setError('');
    try{
      const response=await http.post(`${basePath}/climate-automations/${row.id}/relay`,
        {state:nextState},{withCredentials:true});
      if(response.data?.ok===false)throw new Error(response.data.error||'릴레이 제어 실패');
      // 실제 상태는 기존 MQTT WebSocket 이벤트로만 반영한다.
    }catch(e){setError(e.response?.data?.error||e.message||'릴레이 제어 실패');}
    finally{setBusyRowIds(ids=>ids.filter(id=>id!==row.id));setBusyRelayIds(ids=>ids.filter(id=>id!==row.id));}
  };
  const edit=row=>{setEditing(row.id);setForm({...form,...row});};
  if(!siteId || (!showAddButton && items.length===0 && !error))return null;
  const isCollapsed = id => collapsedIds === null || collapsedIds.has(id);
  const toggleCollapsed = id => setCollapsedIds(prev => {
    const next = new Set(prev || items.map(item => item.id));
    if(next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = row => { edit(row); setDialogOpen(true); };
  const closeDialog = () => { setDialogOpen(false); setEditing(null); };
  const submitForm = async () => { await create(); }; // Original create handler is unchanged.
  return <section className="mb-3" aria-label="자동온도 및 자동습도">
    {showAddButton && <div className="d-flex align-items-center justify-content-between gap-2 mb-3 px-1">
      <div className="fw-bold">자동온도 · 자동습도</div>
      <button type="button" className="btn btn-sm btn-outline-secondary fw-semibold flex-shrink-0" style={{minHeight:38}} onClick={openAdd} disabled={busy}>＋ 자동화 추가</button>
    </div>}
    <div style={items.length===0 && showAddButton ? {border:'1px solid #dee2e6',borderRadius:18,background:'#fff',padding:16} : undefined}>
      {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
      {items.length===0 ? (showAddButton ? <div className="bg-light rounded-3 p-3 text-center text-secondary small">등록된 자동온도·자동습도가 없습니다. 센서와 릴레이를 선택해 추가하세요.</div> : null) : ['temperature','humidity'].map(kind => {
        const groupItems=items.filter(row=>row.kind===kind);
        if(!groupItems.length)return null;
        return <div key={kind} className="mb-3" style={{border:"1px solid #dee2e6",borderRadius:18,background:"#fff",overflow:"hidden"}}>
          {groupItems.map((row,index)=>{
        const collapsed=isCollapsed(row.id);
        const unit=row.kind==='temperature'?'°C':'%';
        return <div key={row.id} className={`p-3 p-md-4 ${index < groupItems.length-1 ? "border-bottom" : ""}`}>
          <div className="d-flex align-items-center justify-content-between gap-2">
            <button type="button" className="border-0 bg-transparent p-0 text-start min-w-0 flex-grow-1 d-flex align-items-center" onClick={()=>toggleCollapsed(row.id)} aria-expanded={!collapsed} aria-label={`${row.name} ${collapsed?'펼치기':'접기'}`}>
              <div className="d-flex align-items-center gap-2 flex-wrap w-100">
                <span className="fw-semibold">{row.name}</span>
                <span className={`fw-semibold ${row.active?'text-success':'text-secondary'}`} style={{whiteSpace:'nowrap'}}>{row.active?'실행 중':'중지'}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{color:'#6c757d',transform:collapsed?'rotate(-90deg)':'rotate(0deg)',transition:'transform .18s'}}><path d="m6 9 6 6 6-6"/></svg>
              </div>
            </button>
            <div className="d-flex align-items-center gap-1 flex-shrink-0">
              <button type="button" className="btn btn-sm border-0 bg-transparent p-2 d-flex align-items-center justify-content-center" title="설정" aria-label={`${row.name} 설정`} style={{color:'#6c757d',opacity:1,cursor:busy||busyRowIds.includes(row.id)||row.active?'not-allowed':'pointer'}} disabled={busy||busyRowIds.includes(row.id)||row.active} onClick={()=>openEdit(row)}>
                <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8.92 4a1.65 1.65 0 0 0 1-1.51V2.4a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 4a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.12.6.65 1.03 1.27 1.03H21a2 2 0 1 1 0 4h-.33c-.62 0-1.15.43-1.27.97Z" />
                          </svg>
              </button>
              <button type="button" className={`btn btn-sm fw-semibold ${row.active?'btn-outline-secondary':'btn-primary'}`} disabled={busy||busyControlIds.includes(row.id)} onClick={()=>act(row,'control')}>{row.active?'중지':'실행'}</button>
            </div>
          </div>
          {!collapsed&&<div className="mt-3">
            <div className="small text-secondary mb-2">
              목표 {row.setpoint}{unit} · 히스테리시스 {row.hysteresis}{unit} · {row.direction==='raise'?(row.kind==='temperature'?'난방':'가습'):(row.kind==='temperature'?'냉방':'제습')}
            </div>
            <div className="row g-2">
              <div className="col-6">
                <div className="bg-light rounded-3 h-100 p-3 text-center">
                  <div className="small text-secondary text-truncate">{row.sensor_entity_name || (row.kind==='temperature'?'현재 온도':'현재 습도')}</div>
                  <div className="fw-bold mt-2" style={{fontSize:'1.45rem'}}>
                    {row.sensor_value !== null && row.sensor_value !== undefined && Number.isFinite(Number(row.sensor_value)) ? `${Number(row.sensor_value).toFixed(1)} ${unit}` : '-'}
                  </div>
                </div>
              </div>
              <div className="col-6">
                <button type="button" className="border-0 bg-light rounded-3 h-100 w-100 p-3 text-center d-flex flex-column align-items-center justify-content-center"
                  disabled={busy || busyRelayIds.includes(row.id) || row.active}
                  onClick={()=>toggleRelay(row)}
                  title={row.active?'자동화 실행 중에는 수동 조작할 수 없습니다':`${row.relay_entity_name || '릴레이'} ${row.relay_state==='on'?'끄기':'켜기'}`}
                  aria-label={`${row.relay_entity_name || '릴레이'} ${row.relay_state==='on'?'끄기':'켜기'}`}
                  style={{minHeight:'100%',cursor:busy||busyRelayIds.includes(row.id)||row.active?'not-allowed':'pointer'}}>
                  <div className="small text-secondary text-truncate w-100">{row.relay_entity_name || '릴레이'}</div>
                  <div className="fw-bold mt-2" style={{fontSize:'1.2rem'}}>
                    {row.relay_state === 'on' ? '켜짐' : row.relay_state === 'off' ? '꺼짐' : '-'}
                  </div>
                </button>
              </div>
            </div>
          </div>}
        </div>;
          })}
        </div>;
      })}
    </div>
    {dialogOpen&&<div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-2 p-sm-3" style={{zIndex:2050,background:'rgba(0,0,0,.55)'}} role="presentation" onMouseDown={e=>{if(e.target===e.currentTarget)closeDialog();}}>
      <div className="bg-white rounded-4 shadow w-100 d-flex flex-column" style={{maxWidth:650,maxHeight:'92dvh',overflow:'hidden'}} role="dialog" aria-modal="true" aria-label={editing?'자동화 설정':'자동화 추가'}>
        <div className="d-flex align-items-center justify-content-between p-3 p-md-4 border-bottom"><div className="fw-bold fs-5">{editing?'자동화 설정':'자동화 추가'}</div><button type="button" className="btn-close" aria-label="닫기" onClick={closeDialog}/></div>
        <div className="p-3 p-md-4 overflow-auto">
          {error&&<div className="alert alert-danger py-2">{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6"><label className="form-label fw-semibold small">자동화 종류</label><select className="form-select" disabled={!!editing} value={form.kind} onChange={e=>setForm(f=>({...f,kind:e.target.value,name:e.target.value==='temperature'?'자동온도':'자동습도',sensor_device_id:'',sensor_entity_id:''}))}><option value="temperature">자동온도</option><option value="humidity">자동습도</option></select></div>
            <div className="col-12 col-sm-6"><label className="form-label fw-semibold small">이름</label><input className="form-control" value={form.name} onChange={e=>change('name',e.target.value)}/></div>
            {!editing&&<>
              <div className="col-12"><label className="form-label fw-semibold small">센서</label><select className="form-select" value={`${form.sensor_device_id}:${form.sensor_entity_id}`} onChange={e=>{const [device,entity]=e.target.value.split(':');setForm(f=>({...f,sensor_device_id:device,sensor_entity_id:entity}));}}><option value=":">센서 선택</option>{sensors.filter(s=>s.kind===form.kind).map(s=><option key={`${s.device_id}:${s.entity_id}`} value={`${s.device_id}:${s.entity_id}`}>{s.device_name} · {s.entity_name}</option>)}</select></div>
              <div className="col-12"><label className="form-label fw-semibold small">릴레이</label><select className="form-select" value={`${form.relay_device_id}:${form.relay_entity_id}`} onChange={e=>{const [device,entity]=e.target.value.split(':');setForm(f=>({...f,relay_device_id:device,relay_entity_id:entity}));}}><option value=":">릴레이 선택</option>{relays.map(r=><option key={`${r.device_id}:${r.entity_id}`} value={`${r.device_id}:${r.entity_id}`}>{r.device_name} · {r.entity_name}</option>)}</select></div>
            </>}
            <div className="col-6"><label className="form-label fw-semibold small">목표값 ({form.kind==='temperature'?'°C':'%'})</label><input type="number" step="0.1" className="form-control" value={form.setpoint} onChange={e=>change('setpoint',e.target.value)}/></div>
            <div className="col-6"><label className="form-label fw-semibold small">히스테리시스</label><input type="number" min="0.1" step="0.1" className="form-control" value={form.hysteresis} onChange={e=>change('hysteresis',e.target.value)}/></div>
            <div className="col-6"><label className="form-label fw-semibold small">제어 방향</label><select className="form-select" value={form.direction} onChange={e=>change('direction',e.target.value)}><option value="raise">{form.kind==='temperature'?'난방':'가습'}</option><option value="lower">{form.kind==='temperature'?'냉방':'제습'}</option></select></div>
            <div className="col-6"><label className="form-label fw-semibold small">검사주기 (초)</label><input type="number" min="1" max="300" className="form-control" value={form.update_period} onChange={e=>change('update_period',e.target.value)}/></div>
          </div>
        </div>
        <div className="p-3 border-top d-flex align-items-center gap-2">
          {editing && <button type="button" className="btn btn-outline-danger me-auto" disabled={busy || busyRowIds.includes(editing) || items.some(item => item.id === editing && item.active)} onClick={async()=>{const row=items.find(item=>item.id===editing);if(!row)return;await act(row,'delete');closeDialog();}}>삭제</button>}
          <div className="ms-auto d-flex gap-2"><button type="button" className="btn btn-outline-secondary" onClick={closeDialog}>취소</button><button type="button" className="btn btn-primary" disabled={busy||(!editing&&(!form.sensor_entity_id||!form.relay_entity_id))} onClick={submitForm}>{busy?'처리 중':editing?'설정 수정':'자동화 추가'}</button></div></div>
      </div>
    </div>}
  </section>;
}
